import { useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./MissingOne.css";

const SYMBOLS = ["🐶", "🐱", "🐭", "🐰", "🦊", "🐻", "🐼", "🐸", "🐵", "🐧", "🦁", "🐯"];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    leftCount: 4,
  },
  medium: {
    label: "Media",
    totalRounds: 10,
    leftCount: 6,
  },
  hard: {
    label: "Difícil",
    totalRounds: 12,
    leftCount: 8,
  },
};

function shuffle(list) {
  return [...list].sort(() => Math.random() - 0.5);
}

function createRound(config) {
  const leftItems = shuffle(SYMBOLS).slice(0, config.leftCount);
  const missingItem = leftItems[Math.floor(Math.random() * leftItems.length)];
  const rightItems = shuffle(leftItems.filter((item) => item !== missingItem));

  return {
    leftItems: shuffle(leftItems),
    rightItems,
    missingItem,
  };
}

export default function MissingOne() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correct, setCorrect] = useState(0);
  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(() => {
    if (!totalRounds) return 0;
    return Math.round((roundIndex / totalRounds) * 100);
  }, [roundIndex, totalRounds]);

  const accuracy =
    totalRounds > 0 ? Math.round((correct / totalRounds) * 100) : 0;

  const averageReactionTime =
    responses.length > 0
      ? Math.round(
          responses.reduce((acc, item) => acc + item.reactionTime, 0) /
            responses.length
        )
      : 0;

  const totalTime =
    startTime && endTime ? Math.round((endTime - startTime) / 1000) : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;
    const now = Date.now();

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);
    setRoundIndex(0);
    setCorrect(0);
    setErrors(0);
    setResponses([]);
    setStarted(true);
    setFinished(false);
    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
    setRound(createRound(nextConfig));
  };

  const answer = (item) => {
    if (!started || finished || !round || !config) return;

    const now = Date.now();
    const reactionTime = now - roundStartTime;
    const isCorrect = item === round.missingItem;

    setResponses((prev) => [
      ...prev,
      {
        round: roundIndex + 1,
        answer: item,
        correctAnswer: round.missingItem,
        isCorrect,
        reactionTime,
      },
    ]);

    if (isCorrect) setCorrect((prev) => prev + 1);
    else setErrors((prev) => prev + 1);

    const nextRound = roundIndex + 1;

    if (nextRound >= config.totalRounds) {
      setFinished(true);
      setStarted(false);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRound);
    setRound(createRound(config));
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setStarted(false);
    setFinished(false);
    setRound(null);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Falta uno"
        description="Compara los dibujos de ambos lados y pulsa cuál falta en el lado derecho."
        areas={["Atención selectiva", "Rastreo visual", "Escaneo bilateral"]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="missing-one-page">
      <div className="missing-one-card">
        <header className="missing-one-header">
          <div>
            <h1>Falta uno</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="missing-one-instruction">
              <h2>¿Cuál de la izquierda falta en la derecha?</h2>
            </section>

            <section className="missing-one-stage">
              <div className="side-panel">
                <span className="side-title">Izquierda</span>

                <div className="items-grid">
                  {round.leftItems.map((item, index) => (
                    <button
                      key={`${item}-${index}`}
                      type="button"
                      className="item-button"
                      onClick={() => answer(item)}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              <div className="side-panel right-panel">
                <span className="side-title">Derecha</span>

                <div className="items-grid">
                  {round.rightItems.map((item, index) => (
                    <div key={`${item}-${index}`} className="item-display">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="missing-one-score">
              <span>Aciertos: {correct}</span>
              <span>Errores: {errors}</span>
              <span>Precisión: {accuracy}%</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                { label: "Aciertos", value: correct },
                { label: "Errores", value: errors },
                { label: "Precisión", value: `${accuracy}%` },
                { label: "Tiempo medio", value: `${averageReactionTime} ms` },
                { label: "Tiempo total", value: `${totalTime}s` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}